/**
 * "POLICY IN FORCE" — and the honest version of that heading is "the policy
 * as these decisions show it".
 *
 * THERE IS NO POLICY ENDPOINT. The auto-approval policy is a code constant in
 * the API (`SYNC_AUTO_APPROVAL_POLICY` in SyncDecisionPolicy.ts): no route
 * serves it, no partner field holds it, and nothing in the dashboard can read
 * it. So the design's panel — a mode, a rule and a basis per partner — has no
 * configuration source to render.
 *
 * What every decision DOES carry is the policy as it applied to that decision:
 * `auto_approval_enabled` snapshotted at decision time (stored per record
 * exactly so flipping the flag later cannot rewrite history) and the
 * thresholds the rules measured against (`rules_fired[].threshold`, stored so
 * that "a threshold change is readable"). Both are the real policy, observed
 * rather than declared.
 *
 * So this panel renders observations and SAYS they are observations. The
 * difference matters: a partner with no rows in the window is absent from this
 * list, which is not the same as being switched off, and the panel must not
 * let a reader conclude otherwise.
 *
 * THE PILL SAYS WHAT THE RECORD SAYS, AND THE RECORD NOW SAYS TWO THINGS.
 * It used to be one boolean, so the pill was that boolean. A decision also
 * carries `rate_sheet` — whose prices the offer was when it was taken — and
 * for a partner whose prices we published the margin rules are measured,
 * recorded and NOT allowed to refuse. "Auto-approval on" alone would invite
 * a reader to assume a floor had been cleared, so that combination has a pill
 * of its own. It is still read off the decisions; it is not a declared mode.
 *
 * Nothing here fills a gap, and nothing here collapses two absences into one.
 * No floor observed is a dash, not an assumed −5% — and "no floor applies,
 * because we set the prices" is a different sentence from "no floor was
 * measured against", which is the distinction `floorSentence` below exists
 * for.
 */

import React from 'react';
import { Badge } from 'react-bootstrap';
import { formatFractionAsPct } from '../../presentation';
import { POLICY_BASIS, POLICY_MODE_PILLS, observedPolicy } from '../decisionFacts';

/**
 * THE FLOOR — AND THE TWO DIFFERENT REASONS THERE MIGHT NOT BE ONE.
 *
 * Both of them render a sentence with no number in it, and they must never be
 * the same sentence:
 *
 *   NO FLOOR APPLIES is a deliberate policy. We published this partner's rate
 *   sheet, so there is no floor to state and the margin rules were stood down
 *   (`advisory: true` on the record, with the measurement still on it). The
 *   margin is not being ignored; it is being honoured.
 *
 *   NO FLOOR OBSERVED is a gap in what we read. Nothing in this window
 *   carried a margin threshold, which says nothing at all about whether one
 *   applies.
 *
 * Collapsed into one dash they would read as each other — "we are not
 * checking the margin" for a partner whose margin gates in full, and "no
 * floor applies" for one whose floor we merely did not see. The panel's whole
 * claim is that it reports observations honestly, so this is the sentence that
 * has to earn it.
 *
 * A window that spans the change gets its own line rather than either: the
 * newest decisions on this arm carry no threshold while an older one in the
 * same window does, and reporting only the newest would hide a floor that was
 * genuinely in force for part of it.
 */
const floorSentence = (partner) => {
  if (partner.basis === POLICY_BASIS.CHANGED) {
    return `Whose prices these were changed in this window: some decisions were taken off a rate sheet of ours, where no floor applies, and some against a floor${partner.floor === null ? '' : ` of ${formatFractionAsPct(partner.floor)}`}. No single basis describes the window.`;
  }

  if (partner.basis === POLICY_BASIS.RATE_SHEET) {
    const applies = 'No margin floor applies — we publish this partner’s prices, so the margin is still measured and recorded but cannot hold an offer.';

    return partner.floor === null
      ? applies
      : `${applies} An older decision in this window was measured against a floor of ${formatFractionAsPct(partner.floor)}.`;
  }

  if (partner.floor !== null) return `Margin floor ${formatFractionAsPct(partner.floor)}.`;

  return 'No margin floor was measured against on these decisions.';
};

function PolicyInForce({ rows, partners, heading = 'Policy in force' }) {
  const observed = observedPolicy(rows, partners);

  return (
    <section className="auto-approval-panel">
      <h2 className="auto-approval-panel-title">{heading}</h2>
      <p className="auto-approval-panel-blurb">
        Read back off the decisions themselves — the flag as it stood when each
        was taken, and the thresholds its rules measured against. There is no
        settings endpoint for the policy, so a partner with no decisions in
        this window is absent below rather than shown as switched off.
      </p>

      {observed.length === 0 ? (
        <p className="mb-0">No decisions in this window, so there is nothing to read the policy off.</p>
      ) : (
        <ul className="auto-approval-policy-list">
          {observed.map((partner) => {
            const pill = POLICY_MODE_PILLS[partner.mode];
            return (
              <li key={partner.partnerId || partner.partnerName}>
                <div className="auto-approval-policy-head">
                  <span className="auto-approval-policy-partner">
                    {partner.partnerName || '(unidentified partner)'}
                  </span>
                  <Badge bg={pill.bg} text={pill.text} className="sync-status">
                    {pill.label}
                  </Badge>
                </div>
                <div className="auto-approval-policy-rule">
                  {floorSentence(partner)}
                  {/* The ceiling gates EVERYBODY, rate sheet or not — it is a
                      blast-radius cap and not a view on the price — so it is
                      printed beside either floor sentence. */}
                  {partner.ceiling !== null && ` Price ceiling ${partner.ceiling}.`}
                </div>
                <div className="auto-approval-policy-basis">
                  {[
                    `${partner.decisions} ${partner.decisions === 1 ? 'decision' : 'decisions'} seen`,
                    partner.acceptRate === null
                      ? null
                      : `accept rate ${formatFractionAsPct(partner.acceptRate)}`,
                  ].filter(Boolean).join(' · ')}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Metric NAMES, not values. These are the CloudWatch metrics the
          decision service emits (verified against SyncDecisionService.ts); no
          endpoint serves their values, so the panel names what to look for
          rather than printing a number it cannot read. */}
      <div className="auto-approval-metrics">
        <div className="auto-approval-metrics-title">What is emitted</div>
        <p className="auto-approval-panel-blurb">
          One metric per outcome, so a silent change in behaviour shows up
          without reading logs. These are CloudWatch metric names — their
          values are not on any endpoint this screen can call.
        </p>
        {[
          'syncAutoApproved',
          'syncHeldForReview',
          'syncPolicyDisagreement',
          'syncPartnerAcceptFailed',
          'syncDecisionLatencyMs',
        ].map((metric) => (
          <code className="auto-approval-metric" key={metric}>{metric}</code>
        ))}
      </div>

    </section>
  );
}

export default PolicyInForce;
